import React, { useState, useEffect } from 'react';
import { X, AlertTriangle } from 'lucide-react';
import posthog from 'posthog-js';
import { isEmulating, getEmulatedUser, getOriginalUserSettings, stopEmulation } from '../utils/emulation';

const EmulationBanner = () => {
  const [emulating, setEmulating] = useState(false);
  const [emulatedUser, setEmulatedUser] = useState(null);

  useEffect(() => {
    // Check emulation status on mount and when localStorage changes
    const checkEmulation = () => {
      const isCurrentlyEmulating = isEmulating();
      setEmulating(isCurrentlyEmulating);
      setEmulatedUser(getEmulatedUser());

      // Add/remove body padding when emulation state changes
      if (isCurrentlyEmulating) {
        document.body.style.paddingTop = '44px';
      } else {
        document.body.style.paddingTop = '';
      }
    };

    checkEmulation();

    // Listen for storage events (in case emulation is started/stopped in another tab)
    window.addEventListener('storage', checkEmulation);

    return () => {
      window.removeEventListener('storage', checkEmulation);
      // Clean up body padding on unmount
      document.body.style.paddingTop = '';
    };
  }, []);

  const handleExitEmulation = () => {
    // Clean up body padding before redirect
    document.body.style.paddingTop = '';

    // Grab admin settings before stopEmulation clears them
    const originalSettings = getOriginalUserSettings();

    stopEmulation();

    // Re-identify PostHog as the original admin so feature flags revert
    if (originalSettings?.email) {
      posthog.identify(originalSettings.email, { email: originalSettings.email, name: originalSettings.firstName });
      posthog.setPersonPropertiesForFlags({ email: originalSettings.email, name: originalSettings.firstName });
    }

    // Redirect to admin panel and reload to apply the original user context
    window.location.href = '/admin/users';
  };

  if (!emulating || !emulatedUser) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-purple-600 text-white px-4 py-2 shadow-lg">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle size={20} className="text-yellow-300" />
          <span className="font-medium">
            Emulation Mode: Viewing as{' '}
            <span className="font-bold">
              {emulatedUser.firstName} {emulatedUser.lastName}
            </span>{' '}
            ({emulatedUser.email})
          </span>
        </div>
        <button
          onClick={handleExitEmulation}
          className="flex items-center gap-2 bg-white text-purple-600 px-4 py-1 rounded-md font-medium hover:bg-purple-100 transition-colors"
        >
          <X size={16} />
          Exit Emulation
        </button>
      </div>
    </div>
  );
};

export default EmulationBanner;
